'use client';

import { useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { AdornedInput, Box, Button, Dropdown, Slider, Text } from '@zillow/constellation-10';
import { ChevronDown } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useFilterLabels } from '@/src/lib/filters/useFilterLabels';
import type { FilterState } from '@/src/lib/filters/filterTypes';
import { FILTER_DROPDOWN_MIN_WIDTH } from './listingSearchConstants';

const PRICE_MIN = 0;
const PRICE_MAX = 10000;
const PRICE_STEP = 100;

type PriceDropdownProps = {
  value: FilterState['price'];
  onChange: (next: FilterState['price']) => void;
  onReset: () => void;
  size?: 'sm' | 'md' | 'lg';
};

function formatInputValue(num: number | null): string {
  if (num === null) return '';
  return num.toLocaleString('en-US');
}

function parseInputValue(raw: string): number | null {
  const digits = raw.replace(/[^0-9]/g, '');
  return digits === '' ? null : parseInt(digits, 10);
}

/**
 * Price filter trigger + popover, built entirely from Constellation
 * primitives — `Dropdown` (trigger + portal + content + footer),
 * `Button` (trigger label + footer actions), `Input` (min/max),
 * `Slider`, `Text`, `Icon`. No HotPads custom wrappers.
 */
export default function PriceDropdown({ value, onChange, onReset, size = 'lg' }: PriceDropdownProps) {
  const tFilters = useTranslations('filters');
  const tSearch = useTranslations('search');
  const { getPriceButtonLabel } = useFilterLabels();
  const [isOpen, setIsOpen] = useState(false);

  const hasActive = value.min !== null || value.max !== null;
  const label = getPriceButtonLabel(value);

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, min: parseInputValue(e.target.value) });
    },
    [onChange, value],
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, max: parseInputValue(e.target.value) });
    },
    [onChange, value],
  );

  const handleSliderChange = useCallback(
    (next: number | Array<number>) => {
      if (!Array.isArray(next)) return;
      const [lo, hi] = next;
      onChange({
        min: lo === PRICE_MIN ? null : lo,
        max: hi === PRICE_MAX ? null : hi,
      });
    },
    [onChange],
  );

  const sliderValue: [number, number] = [value.min ?? PRICE_MIN, value.max ?? PRICE_MAX];
  const sliderTooltipPortal = useRef<HTMLDivElement>(null);

  return (
    <Dropdown.Root open={isOpen} onOpenChange={setIsOpen} placement="bottom-start">
      <Dropdown.Trigger>
        <Button.Root
          tone={hasActive ? 'brand' : 'neutral'}
          emphasis="bare"
          size={size}
          css={{
            // Match the Location combobox's pill radius at every size.
            borderRadius: 'pill',
            justifyContent: 'space-between',
          }}
        >
          <Button.Label
            css={{
              fontWeight: 'bold',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
              // On the large homepage variant, pin to the longest possible
              // label so the trigger doesn't reflow as it changes between
              // "Any price", "Over/Under $X", and "$X - $Y". The compact
              // header uses the label's natural width. `ch` is sized off the
              // '0' glyph, so don't
              // equate ch count with character count: an exhaustive
              // canvas-measureText sweep of every abbreviatePrice() range
              // (it's not the $10k boundary -- that rounds to the *shorter*
              // "$10k" -- but a tied cluster of "$D.Dk - $D.Dk" combos) found
              // the actual widest render is "$5.5k - $5.6k" (~97px at lg's
              // 16px/bold/tabular-nums). 10ch (~99px) covers it with a
              // ~2px buffer; `minWidth` grows past that instead of clipping
              // if real-world rendering needs more, so the buffer can stay
              // thin. Sized on this element specifically because `ch`
              // resolves against its OWN font -- Button.Root inherits a
              // plain 16px/regular font, not this Label's bold one.
              minWidth: size === 'lg' ? '10ch' : undefined,
            }}
          >
            {label}
          </Button.Label>
          <Button.Icon>
            <LucideIcon icon={ChevronDown} size="sm" />
          </Button.Icon>
        </Button.Root>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content style={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}>
          <Dropdown.Body>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
              <Text asChild textStyle="body-sm-bold" color="text.subtle">
                <p>{tFilters('priceRange')}</p>
              </Text>
              <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
                <AdornedInput
                  size="sm"
                  style={{ width: '11ch' }}
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                  input={
                    <AdornedInput.Input
                      type="text"
                      inputMode="numeric"
                      placeholder={tFilters('noMin')}
                      aria-label={tSearch('minimumPrice')}
                      value={formatInputValue(value.min)}
                      onChange={handleMinChange}
                      css={{ fontVariantNumeric: 'tabular-nums' }}
                    />
                  }
                />
                <Text textStyle="body-sm" color="text.subtle">
                  {tFilters('to')}
                </Text>
                <AdornedInput
                  size="sm"
                  style={{ width: '11ch' }}
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                  input={
                    <AdornedInput.Input
                      type="text"
                      inputMode="numeric"
                      placeholder={tFilters('noMax')}
                      aria-label={tSearch('maximumPrice')}
                      value={formatInputValue(value.max)}
                      onChange={handleMaxChange}
                      css={{ fontVariantNumeric: 'tabular-nums' }}
                    />
                  }
                />
              </Box>
              <Box css={{ paddingTop: 'tighter', paddingInline: 'tighter' }}>
                {/* tooltip portal target — keeps the drag tooltip out of the visible DOM */}
                <div ref={sliderTooltipPortal} style={{ display: 'none' }} aria-hidden="true" />
                <Slider.Root
                  min={PRICE_MIN}
                  max={PRICE_MAX}
                  step={PRICE_STEP}
                  value={sliderValue}
                  onValueChange={handleSliderChange}
                  ariaLabel={({ index }) => (index === 0 ? tSearch('minimumPrice') : tSearch('maximumPrice'))}
                  ariaValuetext={({ valueNow }) => `$${valueNow.toLocaleString('en-US')}`}
                >
                  <Slider.Track>
                    <Slider.ActiveTrack />
                  </Slider.Track>
                  <Slider.Handle tooltipPortalRoot={sliderTooltipPortal} />
                  <Slider.Handle tooltipPortalRoot={sliderTooltipPortal} />
                </Slider.Root>
              </Box>
            </Box>
          </Dropdown.Body>
          <Dropdown.Footer
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: hasActive ? 'space-between' : 'flex-end',
              gap: 'tighter',
            }}
          >
            {hasActive ? (
              <Button tone="neutral" emphasis="outlined" size="sm" onClick={onReset}>
                {tFilters('reset')}
              </Button>
            ) : null}
            <Dropdown.Close>
              <Button tone="brand" emphasis="filled" size="sm">
                {tFilters('done')}
              </Button>
            </Dropdown.Close>
          </Dropdown.Footer>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
